import React, { useContext, useEffect, useRef, useState } from "react"
import { useMutation, useQueryClient } from "react-query"
import { mutationRequest } from "../../../util/request"
import { Button, SubmitButton } from "../../shared/Button"
import { Dialog } from "@reach/dialog"
import "@reach/dialog/styles.css"
import {
  Elements,
  useStripe,
  useElements,
  CardElement,
} from "@stripe/react-stripe-js"
import { loadStripe, Stripe } from "@stripe/stripe-js"
import { format } from "date-fns"
import CardSection from "../CardSection"
import { UserContext } from "../../HomeApp"
import { useMyDonationsForPost } from "../../../hooks/useMyDonationsForPost"
import { formatPrice } from "../../../util/ticketUtils"

const DONATION_MIN_AMOUNT = 5
const DONATION_MAX_AMOUNT = 100
const SUGGESTED_DONATION_AMOUNTS = [5, 10, 20]

interface Tip {
  id: number
  payment_method?: string | null
  client_secret?: string | null
}

interface PostDonationComponentProps {
  postId: number
  bandId: number
  postTitle: string
  userName: string
  userEmail: string
  userDefaultPaymentMethodLastFour: string
  donationText?: string | null
}

// A standalone "Donate" button for events with accepts_donation set (Free
// pricing type only) — separate from and unrelated to the RSVP/Going
// mechanism, with its own behavior. Opens a donation-only modal — no ticket
// language anywhere, since a donation here is a Tip, not a TicketPurchase.
export const PostDonationComponent: React.FC<PostDonationComponentProps> = ({
  postId,
  bandId,
  postTitle,
  userName,
  userEmail,
  userDefaultPaymentMethodLastFour,
  donationText,
}) => {
  const { spk } = useContext(UserContext)
  const stripePromiseRef = useRef<Promise<Stripe | null>>()
  const [showDialog, setShowDialog] = useState(false)
  const [amount, setAmount] = useState<number | "">(DONATION_MIN_AMOUNT)
  const [tip, setTip] = useState<Tip | null>(null)
  const [lastFour, setLastFour] = useState(userDefaultPaymentMethodLastFour)
  const [completed, setCompleted] = useState(false)

  useEffect(() => {
    stripePromiseRef.current = loadStripe(spk)
  }, [spk])

  const tipMutation = useMutation(mutationRequest)
  const queryClient = useQueryClient()
  const { data: donationsData } = useMyDonationsForPost(postId, showDialog)
  const pastDonations = donationsData?.tips ?? []

  const openDialog = () => {
    setShowDialog(true)
  }

  const closeDialog = () => {
    setShowDialog(false)
    setTip(null)
    setAmount(DONATION_MIN_AMOUNT)
    setCompleted(false)
  }

  const submitAmount = async () => {
    const chosenAmount = amount === "" ? DONATION_MIN_AMOUNT : amount
    try {
      const result = await tipMutation.mutateAsync({
        options: { method: "POST" },
        path: `/api/v1/bands/${bandId}/tips`,
        data: {
          tip: { amount: chosenAmount, post_id: postId },
        },
      })
      if (result.success) {
        setTip(result.tip)
      } else {
        alert(
          "We're sorry! There was an error starting your donation. Please try again or contact support@bandnada.com",
        )
      }
    } catch {
      alert(
        "We're sorry! There was an error starting your donation. Please try again or contact support@bandnada.com",
      )
    }
  }

  return (
    <div>
      <button
        type="button"
        className="w-auto px-4 py-2 rounded border border-green-700 text-green-700 font-bold text-base bg-transparent focus:outline-none"
        onClick={openDialog}
      >
        Donate to the Show
      </button>

      {showDialog && (
        <Dialog
          isOpen={showDialog}
          onDismiss={closeDialog}
          aria-label="Donate to the show"
        >
          <div className="flex justify-end">
            <button
              className="close-button focus:outline-none text-lg"
              onClick={closeDialog}
            >
              <span aria-hidden>×</span>
            </button>
          </div>

          <h1 className="border-b-4 border-black mb-6 pb-1 text-xl">
            Donate to <span className="font-bold">{postTitle}</span>
          </h1>

          {completed ? (
            <div>
              <div className="text-green-700 font-bold">
                Thank you for your donation!
              </div>
              <div className="text-sm text-gray-700 mt-1">
                We sent you a receipt to {userEmail}
              </div>
            </div>
          ) : tip ? (
            <Elements stripe={stripePromiseRef.current}>
              <DonationCheckoutForm
                userName={userName}
                lastFour={lastFour}
                setLastFour={setLastFour}
                bandId={bandId}
                tip={tip}
                amount={amount === "" ? DONATION_MIN_AMOUNT : amount}
                onComplete={() => {
                  setCompleted(true)
                  queryClient.invalidateQueries(["myDonations", postId])
                }}
              />
            </Elements>
          ) : (
            <div>
              {donationText && (
                <p className="text-sm text-gray-700 mb-4">{donationText}</p>
              )}
              <div className="mb-3">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-1">
                  Suggested Donation
                </div>
                <div className="flex gap-2">
                  {SUGGESTED_DONATION_AMOUNTS.map((suggested) => (
                    <button
                      key={suggested}
                      type="button"
                      onClick={() => setAmount(suggested)}
                      className={`px-3 py-1 rounded border-2 font-bold text-sm focus:outline-none ${
                        amount === suggested
                          ? "bg-green-700 border-green-700 text-white"
                          : "bg-transparent border-green-700 text-green-700"
                      }`}
                    >
                      ${suggested}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mb-4">
                <div className="flex items-center gap-1">
                  <span className="text-gray-700">$</span>
                  <input
                    type="number"
                    min={DONATION_MIN_AMOUNT}
                    max={DONATION_MAX_AMOUNT}
                    step="1"
                    value={amount}
                    onChange={(e) => {
                      const raw = e.target.value
                      setAmount(raw === "" ? "" : parseInt(raw, 10))
                    }}
                    className="border border-gray-300 rounded px-2 py-2 w-24 text-sm"
                  />
                </div>
                <div className="text-xs text-gray-500 mt-1">$5 - $100</div>
                {amount !== "" && amount < DONATION_MIN_AMOUNT && (
                  <div className="text-xs text-red-600 mt-1">
                    Please enter an amount of at least ${DONATION_MIN_AMOUNT}.
                  </div>
                )}
                {amount !== "" && amount > DONATION_MAX_AMOUNT && (
                  <div className="text-xs text-red-600 mt-1">
                    Please enter an amount of at most ${DONATION_MAX_AMOUNT}.
                  </div>
                )}
              </div>
              <Button
                className="w-auto px-4"
                onClick={submitAmount}
                disabled={
                  tipMutation.isLoading ||
                  amount === "" ||
                  amount < DONATION_MIN_AMOUNT ||
                  amount > DONATION_MAX_AMOUNT
                }
                bg="bg-green-700"
              >
                {tipMutation.isLoading
                  ? "Loading..."
                  : `Donate $${amount === "" ? DONATION_MIN_AMOUNT : amount}`}
              </Button>
              {pastDonations.length > 0 && (
                <>
                  <hr className="my-4 border-gray-200" />
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-1">
                    Your Donations for this Show
                  </div>
                  <ul className="text-sm text-gray-700">
                    {pastDonations.map((donation) => (
                      <li
                        key={donation.id}
                        className="flex justify-between border-b border-gray-100 py-1"
                      >
                        <span>
                          {format(new Date(donation.created_at), "MMM d, yyyy")}
                        </span>
                        <span className="font-bold">
                          {formatPrice(donation.amount)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </Dialog>
      )}
    </div>
  )
}

interface DonationCheckoutFormProps {
  userName: string
  lastFour: string
  setLastFour: (lastFour: string) => void
  bandId: number
  tip: Tip
  amount: number
  onComplete: () => void
}

const DonationCheckoutForm: React.FC<DonationCheckoutFormProps> = ({
  userName,
  lastFour,
  setLastFour,
  bandId,
  tip,
  amount,
  onComplete,
}) => {
  const [errorMessage, setErrorMessage] = useState("")
  const [isSubmitActive, setIsSubmitActive] = useState(true)
  const stripe = useStripe()
  const elements = useElements()
  const mutation = useMutation(mutationRequest)

  const handleUpdateTip = async (paymentMethod: string) => {
    try {
      const result = await mutation.mutateAsync({
        options: { method: "PUT" },
        path: `/api/v1/bands/${bandId}/tips/${tip.id}`,
        data: {
          tip: {
            status: "confirmed_by_user",
            card_confirmed: true,
            payment_method: paymentMethod,
          },
        },
      })
      if (result.tip?.last_four) setLastFour(result.tip.last_four)
      onComplete()
    } catch {
      setIsSubmitActive(true)
      setErrorMessage(
        "There was an error processing your donation. Please try again.",
      )
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitActive(false)

    if (tip.payment_method) {
      handleUpdateTip(tip.payment_method)
    } else {
      if (!stripe || !elements) return
      setErrorMessage("")
      const result = await stripe.confirmCardSetup(tip.client_secret!, {
        payment_method: {
          card: elements.getElement(CardElement)!,
          billing_details: { name: userName },
        },
      })

      if (result.error) {
        setIsSubmitActive(true)
        setErrorMessage(
          "There was an error with your card: " +
            result.error.message +
            ". Please double check it, and resubmit",
        )
      } else {
        handleUpdateTip(result.setupIntent.payment_method as string)
      }
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      {tip.payment_method ? (
        <>
          <div className="mb-4">
            Your card ending in&nbsp;
            <span className="font-bold text-gray-800">{lastFour}</span>
            &nbsp;will be used for payment
          </div>
          <SubmitButton type="submit" active={isSubmitActive} className="">
            Confirm ${amount} Donation
          </SubmitButton>
        </>
      ) : tip.client_secret && stripe && elements ? (
        <>
          {errorMessage ? (
            <div className="text-red-500 text-sm">{errorMessage}</div>
          ) : null}
          <CardSection />
          <SubmitButton type="submit" active={isSubmitActive} className="">
            Confirm ${amount} Donation
          </SubmitButton>
          <div className="text-sm text-gray-600 mt-4">
            I authorize bandNada to send instructions to the financial
            institution that issued my card to take payments from my card that I
            explicitly confirmed in bandNada
          </div>
        </>
      ) : (
        <div>Loading payment form...</div>
      )}
    </form>
  )
}

export default PostDonationComponent
